import { supabase } from "../../lib/supabase";

/*
  EMAIL SERVICE
  
  Tables used: email_campaigns, email_templates, email_logs
*/

/* Get all email campaigns */
export async function getAllCampaigns() {
  try {
    const { data, error } = await supabase
      .from("email_campaigns")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Get campaigns error:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Get campaigns error:", error);
    return [];
  }
}

/* Get campaign by ID */
export async function getCampaignById(id) {
  try {
    const { data, error } = await supabase
      .from("email_campaigns")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Get campaign error:", error);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Get campaign error:", error);
    return null;
  }
}

/* Create email campaign */
export async function createCampaign(campaignData) {
  try {
    const { data, error } = await supabase
      .from("email_campaigns")
      .insert([campaignData])
      .select()
      .single();

    if (error) {
      console.error("Create campaign error:", error);
      return { success: false, message: "Failed to create campaign" };
    }

    return { success: true, message: "Campaign created successfully!", data };
  } catch (error) {
    console.error("Create campaign error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Update campaign */
export async function updateCampaign(id, updates) {
  try {
    const { data, error } = await supabase
      .from("email_campaigns")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Update campaign error:", error);
      return { success: false, message: "Failed to update campaign" };
    }

    return { success: true, message: "Campaign updated successfully!", data };
  } catch (error) {
    console.error("Update campaign error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Delete campaign */
export async function deleteCampaign(id) {
  try {
    const { error } = await supabase
      .from("email_campaigns")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Delete campaign error:", error);
      return { success: false, message: "Failed to delete campaign" };
    }

    return { success: true, message: "Campaign deleted successfully!" };
  } catch (error) {
    console.error("Delete campaign error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Send email campaign via Edge Function */
export async function sendCampaign(campaignId) {
  try {
    const { data, error } = await supabase.functions.invoke(
      "send-email-campaign",
      {
        body: { campaignId },
      }
    );

    if (error) {
      console.error("Send campaign error:", error);
      return {
        success: false,
        message: "Failed to send campaign: " + error.message,
      };
    }

    return {
      success: true,
      message: data.message,
      sentCount: data.sentCount,
      failedCount: data.failedCount,
    };
  } catch (error) {
    console.error("Send campaign error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Get campaign logs */
export async function getCampaignLogs(campaignId) {
  try {
    const { data, error } = await supabase
      .from("email_logs")
      .select("*")
      .eq("campaign_id", campaignId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Get campaign logs error:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Get campaign logs error:", error);
    return [];
  }
}

/* Get all email templates */
export async function getAllTemplates() {
  try {
    const { data, error } = await supabase
      .from("email_templates")
      .select("*")
      .eq("is_active", true)
      .order("category", { ascending: true });

    if (error) {
      console.error("Get templates error:", error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error("Get templates error:", error);
    return [];
  }
}

/* Get template by ID */
export async function getTemplateById(id) {
  try {
    const { data, error } = await supabase
      .from("email_templates")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error("Get template error:", error);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Get template error:", error);
    return null;
  }
}

/* Create email template */
export async function createTemplate(templateData) {
  try {
    const { data, error } = await supabase
      .from("email_templates")
      .insert([templateData])
      .select()
      .single();

    if (error) {
      console.error("Create template error:", error);
      return { success: false, message: "Failed to create template" };
    }

    return { success: true, message: "Template created successfully!", data };
  } catch (error) {
    console.error("Create template error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Update template */
export async function updateTemplate(id, updates) {
  try {
    const { data, error } = await supabase
      .from("email_templates")
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error("Update template error:", error);
      return { success: false, message: "Failed to update template" };
    }

    return { success: true, message: "Template updated successfully!", data };
  } catch (error) {
    console.error("Update template error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Delete template */
export async function deleteTemplate(id) {
  try {
    const { error } = await supabase
      .from("email_templates")
      .delete()
      .eq("id", id);

    if (error) {
      console.error("Delete template error:", error);
      return { success: false, message: "Failed to delete template" };
    }

    return { success: true, message: "Template deleted successfully!" };
  } catch (error) {
    console.error("Delete template error:", error);
    return { success: false, message: "An unexpected error occurred" };
  }
}

/* Get campaign statistics */
export async function getCampaignStats() {
  try {
    const { data, error } = await supabase.rpc("get_campaign_stats");

    if (error) {
      console.error("Get campaign stats error:", error);
      return {
        total_campaigns: 0,
        draft_campaigns: 0,
        sent_campaigns: 0,
        total_emails_sent: 0,
      };
    }

    return data[0] || {
      total_campaigns: 0,
      draft_campaigns: 0,
      sent_campaigns: 0,
      total_emails_sent: 0,
    };
  } catch (error) {
    console.error("Get campaign stats error:", error);
    return {
      total_campaigns: 0,
      draft_campaigns: 0,
      sent_campaigns: 0,
      total_emails_sent: 0,
    };
  }
}

/* Get recipient count for a campaign type */
export async function getRecipientCount(recipientType) {
  try {
    let count = 0;

    if (recipientType === "newsletter") {
      const { count: subscriberCount, error } = await supabase
        .from("newsletter_subscribers")
        .select("*", { count: "exact", head: true })
        .eq("is_active", true);

      if (!error) count = subscriberCount || 0;
    } else if (recipientType === "conference") {
      const { count: regCount, error } = await supabase
        .from("conference_registrations")
        .select("*", { count: "exact", head: true });

      if (!error) count = regCount || 0;
    } else if (recipientType === "club") {
      const { count: memberCount, error } = await supabase
        .from("africamed_club_applications")
        .select("*", { count: "exact", head: true });

      if (!error) count = memberCount || 0;
    }

    return count;
  } catch (error) {
    console.error("Get recipient count error:", error);
    return 0;
  }
}
